import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { useHubMember } from "@/lib/HubMemberContext";
import {
  Calendar, Clock, RefreshCw, Plus, Ticket, Trophy, User,
  ArrowRight, Sparkles, CheckCircle2, AlertCircle, Receipt, Download,
  UserPlus, X, Phone, Wallet as WalletIcon, IndianRupee, FileText, PlusCircle,
} from "lucide-react";
import ParentWardWallet from "@/components/parent/ParentWardWallet";
import PushSubscribeCard from "@/components/push/PushSubscribeCard";

/**
 * Unified Member Portal — one logged-in home for everyone (parents + solo members).
 * All sections auto-hide when empty, so a solo member with no kids sees a clean page.
 */
export default function MemberPortal() {
  const navigate = useNavigate();
  const { ready, member } = useHubMember();
  const [memberships, setMemberships] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [feeSummary, setFeeSummary] = useState([]);
  const [wallets, setWallets] = useState({});   // { [ward_id]: { balance, total_credits, total_debits } }
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddChild, setShowAddChild] = useState(false);
  const [walletWard, setWalletWard] = useState(null);  // { ward_id, ward_name } — opens ParentWardWallet modal
  const [pendingRequests, setPendingRequests] = useState([]);

  useEffect(() => {
    if (!ready) return;
    if (!member) {
      navigate("/member/login", { replace: true });
      return;
    }
    (async () => {
      const safe = async (fn, fallback) => {
        try { return await fn(); } catch (_e) { return fallback; }
      };
      // Load parent + member data in parallel — each block auto-hides when empty.
      const [ms, sm, bk, fs, inv, prs] = await Promise.all([
        safe(() => api.get("/member/memberships").then((r) => r.data), []),
        safe(() => api.get("/member/summary").then((r) => r.data), null),
        safe(() => api.get("/member/reservations").then((r) => r.data), []),
        safe(() => api.get("/parent/fee-summary").then((r) => r.data.rows || []), []),
        safe(() => api.get("/parent/invoices").then((r) => r.data), []),
        safe(() => api.get("/parent/ward-requests/mine").then((r) => r.data), []),
      ]);
      setMemberships(ms || []);
      setSummary(sm);
      setBookings(bk || []);
      setFeeSummary(fs || []);
      setInvoices(inv || []);
      setPendingRequests((prs || []).filter((r) => r.status === "pending"));
      // Fetch a wallet summary for every ward in parallel so the ward cards can
      // show a live Cr / Dr balance and totals — the founder wanted this
      // visible on hostel wards specifically, but it applies to any ward.
      const walletById = {};
      await Promise.all((fs || []).map(async (row) => {
        const w = await safe(
          () => api.get(`/parent/wards/${row.ward_id}/wallet`).then((r) => r.data.summary),
          null,
        );
        if (w) walletById[row.ward_id] = w;
      }));
      setWallets(walletById);
      setLoading(false);
    })();
  }, [ready, member, navigate]);

  if (!ready || loading) {
    return <div className="min-h-[60vh] grid place-items-center text-white/40">Loading your portal…</div>;
  }
  if (!member) return null;

  const active = memberships.filter((m) => m.is_active);
  const inactive = memberships.filter((m) => !m.is_active);

  return (
    <section className="max-w-6xl mx-auto py-10 md:py-14 px-5" data-testid="member-portal">
      {/* Header greeting */}
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-2">
            // Hub ID · {member.hub_id || "—"}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter leading-none">
            Hi {(member.name || "").split(" ")[0] || "member"}.
          </h1>
          <div className="text-sm text-white/60 mt-2">
            {summary?.memberships_active
              ? <>You have <b className="brand-blue">{summary.memberships_active} active plan{summary.memberships_active === 1 ? "" : "s"}</b>{summary?.upcoming_reservations ? <> and <b className="brand-orange">{summary.upcoming_reservations} upcoming booking{summary.upcoming_reservations === 1 ? "" : "s"}</b></> : null}.</>
              : "Buy a plan or book a one-off slot below."}
          </div>
        </div>
        <Link to="/member/profile" data-testid="portal-profile-link"
          className="border border-white/25 text-white/80 px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-white hover:text-black inline-flex items-center gap-2">
          <User size={14} /> My Profile
        </Link>
      </div>

      {/* Push notification opt-in — shown once per session; component
          hides itself when the user has already subscribed or their browser
          doesn't support Web Push. */}
      <PushSubscribeCard testid="member-push-card" />

      {/* Onboarding checklist — only shown for brand-new accounts with nothing set up yet */}
      {(!member.phone_verified || (memberships.length === 0 && feeSummary.length === 0 && bookings.length === 0)) && (
        <div className="mb-8 border-2 border-brand-orange bg-brand-orange/5 p-5" data-testid="portal-onboarding">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="brand-orange" size={20} />
            <div className="font-display text-xl font-black uppercase">Get started in 3 steps</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <OnboardStep
              n={1} title="Verify WhatsApp"
              done={member.phone_verified}
              cta={!member.phone_verified ? "Add phone" : "Verified"}
              to="/member/profile"
              icon={Phone}
            />
            <OnboardStep
              n={2} title="Buy a plan or book a slot"
              done={memberships.length > 0 || bookings.length > 0}
              cta={memberships.length > 0 || bookings.length > 0 ? "Great!" : "Explore"}
              to="/membership"
              icon={Ticket}
            />
            <OnboardStep
              n={3} title="Add your child (for academy)"
              done={feeSummary.length > 0 || pendingRequests.length > 0}
              cta={feeSummary.length > 0 ? "Great!" : pendingRequests.length > 0 ? "Request sent" : "Add a child"}
              onClick={() => setShowAddChild(true)}
              icon={UserPlus}
            />
          </div>
        </div>
      )}

      {/* Pending ward requests notice */}
      {pendingRequests.length > 0 && (
        <div className="mb-8 border border-yellow-500/40 bg-yellow-500/10 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="portal-pending-requests">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-yellow-300">// Awaiting founder approval</div>
            <div className="text-sm mt-0.5">
              You have <b>{pendingRequests.length} pending ward request{pendingRequests.length === 1 ? "" : "s"}</b>:{" "}
              {pendingRequests.map((r) => r.ward_name).join(", ")}. We&apos;ll notify you on WhatsApp once approved.
            </div>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-10">
        <QuickAction
          to="/booking" testId="portal-book-slot"
          icon={Calendar} title="Book a fresh slot"
          desc="Pick a court + time. Pay per hour." />
        <QuickAction
          to="/membership" testId="portal-buy-plan"
          icon={Ticket} title="Buy a membership"
          desc="Save 20–30% vs. rack rate. Locked pricing." tone="orange" />
        <QuickAction
          to="/tournaments" testId="portal-tournaments"
          icon={Trophy} title="Play a tournament"
          desc="Weekend brackets, prize money, rankings." />
      </div>

      {/* Active memberships */}
      <div className="mb-10">
        <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mb-4 flex items-center gap-2">
          <Sparkles className="brand-blue" size={22} /> Your Active Plans
        </h2>
        {active.length === 0 ? (
          <div className="border border-dashed border-white/15 bg-[#131A2B] p-8 text-center">
            <div className="text-white/50 text-sm mb-3">You don&apos;t have an active membership yet.</div>
            <Link to="/membership" className="inline-block bg-brand-orange text-black px-6 py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-white">
              Explore Membership Plans
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {active.map((m) => <MembershipCard key={m.id} m={m} />)}
          </div>
        )}
      </div>

      {/* Expiring / Renew */}
      {inactive.length > 0 && (
        <div className="mb-10">
          <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mb-4 flex items-center gap-2">
            <RefreshCw className="brand-orange" size={22} /> Renew or Buy Again
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {inactive.map((m) => <MembershipCard key={m.id} m={m} expired />)}
          </div>
        </div>
      )}

      {/* Wards & Fee Summary — auto-shown only when the account has children in the academy */}
      {feeSummary.length > 0 && (
        <div className="mb-10" data-testid="portal-wards-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight flex items-center gap-2">
              <User className="brand-orange" size={22} /> My Kids · {feeSummary.length} {feeSummary.length === 1 ? "ward" : "wards"}
            </h2>
            <button onClick={() => setShowAddChild(true)}
              data-testid="portal-add-another-child"
              className="border border-brand-orange text-brand-orange px-3 py-2 text-[11px] font-mono uppercase tracking-widest hover:bg-brand-orange hover:text-black inline-flex items-center gap-1.5">
              <UserPlus size={12} /> Add another child
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {feeSummary.map((w) => (
              <WardMiniCard
                key={w.ward_id}
                data={w}
                wallet={wallets[w.ward_id]}
                onPayFees={() => {
                  const el = document.getElementById("portal-dues") || document.getElementById("portal-invoices");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                onTopUp={() => setWalletWard({ id: w.ward_id, name: w.ward_name })}
              />
            ))}
          </div>
        </div>
      )}

      {/* Pending Dues (not-yet-finalised) — shown separately with clear "pay first" affordance */}
      {invoices.some((i) => !i.is_finalised) && (
        <div className="mb-10" id="portal-dues" data-testid="portal-dues-section">
          <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mb-2 flex items-center gap-2">
            <AlertCircle className="brand-orange" size={22} /> Pending Dues
          </h2>
          <p className="text-xs text-white/50 mb-4">Formal invoice is issued only after full payment is received.</p>
          <div className="border-2 border-brand-orange/30 bg-[#131A2B] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-widest text-white/40 border-b border-white/12">
                  <th className="text-left p-3">For</th>
                  <th className="text-left p-3">Description</th>
                  <th className="text-right p-3">Subtotal</th>
                  <th className="text-right p-3">GST</th>
                  <th className="text-right p-3">Total</th>
                  <th className="text-right p-3">Paid so far</th>
                  <th className="text-right p-3">Balance</th>
                  <th className="text-right p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {invoices.filter((i) => !i.is_finalised).map((inv) => {
                  const paid = Number(inv.amount_paid || 0);
                  const bal = Number(inv.balance_due ?? (Number(inv.total || 0) - paid));
                  return (
                    <tr key={inv.id} className="border-b border-white/6" data-testid={`dues-row-${inv.id}`}>
                      <td className="p-3">{inv.ward_name || "-"}</td>
                      <td className="p-3 text-white/80">{inv.description || inv.service_type}</td>
                      <td className="p-3 text-right font-mono text-white/60">{formatINR(inv.amount || 0)}</td>
                      <td className="p-3 text-right font-mono text-white/60">{formatINR(inv.tax_amount || 0)}</td>
                      <td className="p-3 text-right font-mono">{formatINR(inv.total || 0)}</td>
                      <td className="p-3 text-right font-mono text-green-300">{formatINR(paid)}</td>
                      <td className="p-3 text-right font-mono brand-orange font-bold">{formatINR(bal)}</td>
                      <td className="p-3 text-right">
                        <button
                          onClick={async () => {
                            const { startCheckout } = await import("@/lib/checkout");
                            startCheckout({
                              kind: "invoice", ref_id: inv.id, amount: bal,
                              name: "Fee payment · " + (inv.description || "HTP Sports Hub"),
                              return_to: "/me",
                              onSuccess: () => window.location.reload(),
                            });
                          }}
                          data-testid={`dues-pay-${inv.id}`}
                          className="inline-flex items-center gap-1 bg-brand-orange text-black px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest font-bold hover:bg-white"
                        >
                          Pay {formatINR(bal)}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Finalised invoices (fully paid) — downloadable */}
      {invoices.some((i) => i.is_finalised) && (
        <div className="mb-10" id="portal-invoices" data-testid="portal-invoices-section">
          <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mb-4 flex items-center gap-2">
            <Receipt className="brand-blue" size={22} /> Paid Invoices
          </h2>
          <div className="border border-white/12 bg-[#131A2B] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-widest text-white/40 border-b border-white/12">
                  <th className="text-left p-3">Invoice #</th>
                  <th className="text-left p-3">Issued</th>
                  <th className="text-left p-3">Description</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.filter((i) => i.is_finalised).slice(0, 10).map((inv) => (
                  <tr key={inv.id} className="border-b border-white/6">
                    <td className="p-3 font-mono text-xs">{inv.invoice_number || "—"}</td>
                    <td className="p-3 font-mono text-[11px] text-white/50">
                      {inv.finalised_at ? new Date(inv.finalised_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
                    </td>
                    <td className="p-3">{inv.description || inv.service_type}</td>
                    <td className="p-3 text-right font-mono">{formatINR(inv.total || 0)}</td>
                    <td className="p-3 text-right">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 border border-white/25 px-2 py-1 text-[10px] font-mono uppercase tracking-widest hover:bg-white/10">
                        <Download size={10} /> PDF
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent bookings */}
      <div>
        <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mb-4">Recent Bookings</h2>
        {bookings.length === 0 ? (
          <div className="text-white/40 text-sm">No bookings yet. <Link to="/booking" className="brand-blue underline">Book your first slot</Link>.</div>
        ) : (
          <div className="border border-white/12 bg-[#131A2B] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-widest text-white/40 border-b border-white/12">
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Facility</th>
                  <th className="text-left p-3">Slot</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-left p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.slice(0, 8).map((b) => (
                  <tr key={b.id} className="border-b border-white/6">
                    <td className="p-3 font-mono text-xs">{b.date}</td>
                    <td className="p-3">{b.facility_name || b.facility_slug}</td>
                    <td className="p-3 font-mono text-xs">{b.start_time} – {b.end_time}</td>
                    <td className="p-3 text-right font-mono">{b.amount ? formatINR(b.amount) : "—"}</td>
                    <td className="p-3">
                      <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${b.status === "confirmed" ? "border-green-500/50 text-green-300" : "border-white/15 text-white/50"}`}>
                        {b.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddChild && (
        <AddChildModal onClose={() => setShowAddChild(false)}
          onDone={(newReq) => {
            setShowAddChild(false);
            setPendingRequests([newReq, ...pendingRequests]);
            toast.success("Request sent — the founder will approve shortly");
          }} />
      )}

      {walletWard && (
        <ParentWardWallet
          wardId={walletWard.id}
          wardName={walletWard.name}
          onClose={async () => {
            setWalletWard(null);
            // Refresh the wallet snapshot for this ward so the card reflects the new balance
            try {
              const { data } = await api.get(`/parent/wards/${walletWard.id}/wallet`);
              setWallets((prev) => ({ ...prev, [walletWard.id]: data.summary }));
            } catch (_e) { /* silent */ }
          }}
        />
      )}
    </section>
  );
}

function QuickAction({ to, testId, icon: Icon, title, desc, tone }) {
  return (
    <Link to={to} data-testid={testId}
      className={`border p-5 hover:border-brand-orange transition ${tone === "orange" ? "border-brand-orange bg-brand-orange/5" : "border-white/15 bg-[#131A2B]"}`}>
      <Icon className={tone === "orange" ? "brand-orange" : "brand-blue"} size={24} />
      <div className="font-display text-lg font-black uppercase mt-2">{title}</div>
      <div className="text-xs text-white/60 mt-1">{desc}</div>
      <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest brand-orange">
        Open <ArrowRight size={11} />
      </div>
    </Link>
  );
}

function MembershipCard({ m, expired }) {
  const usesLeft = (m.total_uses != null && m.uses_remaining != null) ? m.uses_remaining : null;
  const hoursLeft = m.hours_remaining ?? null;
  const validUntil = m.valid_until ? new Date(m.valid_until).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : null;
  const planName = m.plan_snapshot?.name || m.plan_name || m.facility_name;
  const facility = m.facility_name || m.facility_slug;
  return (
    <div className={`border p-5 ${expired ? "border-white/12 opacity-70" : "border-brand-blue/40 bg-brand-blue/5"}`} data-testid={`membership-card-${m.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">{facility}</div>
          <div className="font-display text-2xl font-black uppercase mt-0.5">{planName}</div>
        </div>
        {expired ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border border-red-500/50 text-red-300 bg-red-500/10">
            <AlertCircle size={10} /> Expired
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border border-green-500/50 text-green-300 bg-green-500/10">
            <CheckCircle2 size={10} /> Active
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 mt-4 border-t border-white/10 pt-4">
        {hoursLeft != null && (
          <Metric label="Hours left" value={hoursLeft} icon={Clock} />
        )}
        {usesLeft != null && (
          <Metric label="Uses left" value={usesLeft} icon={Ticket} />
        )}
        {validUntil && (
          <Metric label="Valid until" value={validUntil} icon={Calendar} small />
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {!expired && (
          <Link to={`/booking?facility=${m.facility_slug}&plan=${m.id}`}
            data-testid={`book-with-plan-${m.id}`}
            className="flex-1 bg-brand-orange text-black px-4 py-2 font-bold text-xs uppercase tracking-widest text-center hover:bg-white inline-flex items-center justify-center gap-1">
            <Plus size={12} /> Book slot with plan
          </Link>
        )}
        <Link to={`/membership?facility=${m.facility_slug}`}
          data-testid={`renew-plan-${m.id}`}
          className={`px-4 py-2 font-bold text-xs uppercase tracking-widest text-center inline-flex items-center justify-center gap-1 ${expired ? "flex-1 bg-brand-blue text-white hover:bg-white hover:text-black" : "border border-white/25 text-white/70 hover:bg-white/10"}`}>
          <RefreshCw size={12} /> {expired ? "Renew now" : "Renew"}
        </Link>
      </div>

      {m.price && (
        <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-white/40 text-right">
          Paid: <span className="text-white/80">{formatINR(m.price)}</span>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, icon: Icon, small }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 flex items-center gap-1">
        <Icon size={10} /> {label}
      </div>
      <div className={`font-display font-black brand-blue mt-1 ${small ? "text-sm" : "text-2xl"}`}>{value}</div>
    </div>
  );
}

function WardMiniCard({ data, wallet, onPayFees, onTopUp }) {
  const paidTotal = (data.buckets?.registration?.paid || 0) + (data.buckets?.admission?.paid || 0)
                  + (data.buckets?.security?.paid || 0) + (data.buckets?.monthly?.paid || 0);
  const pendingTotal = data.pending_total || 0;
  const status = data.monthly_current;
  const statusMap = {
    paid:       { cls: "border-green-500/50 text-green-300 bg-green-500/10",   label: "Paid" },
    pending:    { cls: "border-yellow-500/50 text-yellow-300 bg-yellow-500/10", label: "Pending" },
    overdue:    { cls: "border-red-500/50 text-red-300 bg-red-500/10",         label: "Overdue" },
    not_billed: { cls: "border-white/15 text-white/50 bg-white/5",             label: "Not billed" },
  };
  const st = statusMap[status] || statusMap.not_billed;
  const walletBal = wallet ? Number(wallet.balance || 0) : null;
  const walletTone = walletBal == null ? "" : walletBal > 0 ? "text-green-300" : walletBal < 0 ? "text-red-300" : "text-white/60";
  const walletLabel = walletBal == null ? "" : walletBal > 0 ? "Cr" : walletBal < 0 ? "Dr" : "";
  return (
    <div className="border border-white/12 bg-[#131A2B] p-4" data-testid={`portal-ward-${data.ward_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-display text-lg font-black uppercase">{data.ward_name}</div>
          <div className="text-[11px] font-mono text-white/50 mt-0.5 uppercase">
            {data.sport}
            {data.batch_name && <span> · {data.batch_name}</span>}
            {!data.active_enrollment && <span className="ml-2 text-red-300">· Withdrawn</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-widest border ${st.cls}`}>
            This month: {st.label}
          </span>
          {pendingTotal > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-red-500/40 bg-red-500/10 text-red-300">
              {data.pending_count} pending · {formatINR(pendingTotal)}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 border-t border-white/10 pt-3">
        <MiniStat label="Reg. paid" value={data.buckets?.registration?.paid || 0} />
        <MiniStat label="Adm. paid" value={data.buckets?.admission?.paid || 0} />
        <MiniStat label="Sec. paid" value={data.buckets?.security?.paid || 0} />
        <MiniStat label="Monthly paid" value={data.buckets?.monthly?.paid || 0} />
      </div>

      {/* Wallet + action row — visible for every ward. Hostel wards especially
          rely on this to see Cr/Dr against room, mess and kit debits. */}
      <div className="mt-3 border-t border-white/10 pt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest">
          <WalletIcon size={14} className="brand-orange" />
          <span className="text-white/50">Wallet:</span>
          {walletBal == null ? (
            <span className="text-white/40">—</span>
          ) : (
            <>
              <span className={`font-bold ${walletTone}`}>{formatINR(Math.abs(walletBal))} {walletLabel}</span>
              <span className="text-white/40 hidden md:inline">
                · Credits {formatINR(wallet.total_credits || 0)} · Debits {formatINR(wallet.total_debits || 0)}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onTopUp}
            data-testid={`ward-topup-${data.ward_id}`}
            className="inline-flex items-center gap-1.5 border border-brand-orange/60 text-brand-orange hover:bg-brand-orange hover:text-black px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest font-bold"
          >
            <PlusCircle size={12} /> Top up
          </button>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/parent/wards/${data.ward_id}/wallet/statement.pdf`}
            target="_blank" rel="noreferrer"
            data-testid={`ward-statement-${data.ward_id}`}
            className="inline-flex items-center gap-1.5 border border-white/25 text-white/70 hover:text-white hover:border-white px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest"
          >
            <FileText size={12} /> Statement
          </a>
          <button
            type="button"
            onClick={onPayFees}
            data-testid={`ward-pay-fees-${data.ward_id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest font-bold ${
              pendingTotal > 0
                ? "bg-brand-orange text-black hover:bg-white"
                : "border border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white"
            }`}
          >
            <IndianRupee size={12} />
            {pendingTotal > 0 ? `Pay ${formatINR(pendingTotal)}` : "Pay fees"}
          </button>
        </div>
      </div>

      <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-white/40 text-right">
        Total paid so far: <span className="brand-blue font-bold">{formatINR(paidTotal)}</span>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-white/50">{label}</div>
      <div className="text-sm font-mono brand-blue mt-0.5">{formatINR(value)}</div>
    </div>
  );
}


function OnboardStep({ n, title, done, cta, to, onClick, icon: Icon }) {
  const inner = (
    <div className={`border p-4 h-full flex flex-col ${done ? "border-green-500/40 bg-green-500/5" : "border-white/20 bg-[#0B1120] hover:border-brand-orange"}`}
      data-testid={`onboard-step-${n}`}>
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded-full grid place-items-center text-[10px] font-black ${done ? "bg-green-500 text-black" : "bg-brand-orange text-black"}`}>
          {done ? <CheckCircle2 size={13} /> : n}
        </div>
        <Icon size={14} className={done ? "text-green-300" : "brand-orange"} />
        <div className="text-sm font-bold">{title}</div>
      </div>
      <div className={`mt-3 text-[11px] font-mono uppercase tracking-widest ${done ? "text-green-300" : "brand-orange"} inline-flex items-center gap-1`}>
        {cta} {!done && <ArrowRight size={11} />}
      </div>
    </div>
  );
  if (to) return <Link to={to}>{inner}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className="text-left">{inner}</button>;
  return inner;
}

function AddChildModal({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", age: "", sport: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Enter the child's name");
    setBusy(true);
    try {
      const { data } = await api.post("/parent/ward-request", {
        name: form.name.trim(),
        age: form.age ? parseInt(form.age, 10) : null,
        sport: form.sport.trim() || null,
        notes: form.notes.trim() || null,
      });
      onDone(data.request);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#131A2B] border border-white/12 p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}
        data-testid="add-child-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Add a child</div>
            <div className="font-display text-2xl font-black uppercase mt-1">New Ward Request</div>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <p className="text-xs text-white/60 mb-4">
          Send us your child&apos;s details. The founder will review, pick the right batch and enrol them — you&apos;ll get a WhatsApp update.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Child&apos;s name</div>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="add-child-name" autoFocus required
              className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-orange" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Age</div>
              <input type="number" min="4" max="25" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })}
                data-testid="add-child-age" placeholder="e.g. 12"
                className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-orange" />
            </label>
            <label className="block">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Sport (optional)</div>
              <input value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })}
                data-testid="add-child-sport" placeholder="Cricket / Football / Swim…"
                className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-orange" />
            </label>
          </div>
          <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Notes (optional)</div>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
              data-testid="add-child-notes"
              placeholder="Preferred batch time, previous experience…"
              className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-orange" />
          </label>
          <div className="flex gap-2 pt-2 border-t border-white/10">
            <button type="button" onClick={onClose} className="flex-1 border border-white/20 py-2.5 text-sm font-mono uppercase tracking-widest">Cancel</button>
            <button type="submit" disabled={busy}
              data-testid="add-child-submit"
              className="flex-1 bg-brand-orange text-black py-2.5 text-sm font-bold uppercase tracking-widest disabled:opacity-50">
              {busy ? "Sending…" : "Send request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

